import { pluralize } from "@/lib/formatters/GeneralStringFormatting";
import { SpaceValues } from "@/lib/utils/SpaceValues";
import styled from "@emotion/styled";
import { Box, Row, Space, Txt } from "@stenajs-webui/core";
import {
  Card,
  CardBody,
  CardHeader,
  Icon,
  stenaAngleDown,
  stenaAngleRight,
} from "@stenajs-webui/elements";
import { StandardTable } from "@stenajs-webui/grid";
import { cssColor } from "@stenajs-webui/theme";
import { testIdConstants } from "@/types/test-id-constants/testIdConstants";
import { KeyboardEvent, useState } from "react";
import { rebateBookingsTableConfig } from "../../config/RebateBookingsTableConfig";
import { formatAmountWithCurrency } from "../../lib/rebateAmountUtils";
import { RouteReservedBookings } from "../../lib/reservedBookingUtils";

/**
 * The card rounds its corners without clipping its children, so the hover
 * background has to round along with it. Collapsed, the header is the whole
 * card and rounds on all four corners; expanded, the table takes over the
 * bottom two.
 */
const ClickableCardHeader = styled.div<{ expanded: boolean }>`
  cursor: pointer;
  border-radius: ${({ expanded }) =>
    expanded
      ? "var(--swui-border-radius) var(--swui-border-radius) 0 0"
      : "var(--swui-border-radius)"};

  &:hover {
    background: ${cssColor("--lhds-color-ui-100")};
  }
`;

/**
 * Animates height without needing to know the content height: the row goes
 * from 0fr to 1fr and the grid resolves the rest.
 */
const ExpandableContent = styled.div<{ expanded: boolean }>`
  display: grid;
  grid-template-rows: ${({ expanded }) => (expanded ? "1fr" : "0fr")};
  transition: grid-template-rows 200ms ease;

  > * {
    min-height: 0;
    overflow: hidden;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

interface Props {
  route: RouteReservedBookings;
  currencyCode: string | null | undefined;
}

export const RebateBookingsForRoute = ({ route, currencyCode }: Props) => {
  const [expanded, setExpanded] = useState(false);
  // The table stays out of the DOM until the route is opened the first time, so
  // a rebate with many routes does not render every booking up front.
  const [everExpanded, setEverExpanded] = useState(false);

  const toggle = () => {
    setEverExpanded(true);
    setExpanded((expanded) => !expanded);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      toggle();
    }
  };

  return (
    <Card>
      <ClickableCardHeader
        expanded={expanded}
        role={"button"}
        tabIndex={0}
        aria-expanded={expanded}
        onClick={toggle}
        onKeyDown={onKeyDown}
        data-testid={`${testIdConstants.rebateBookingsRouteCard}-${route.routeCode}`}
      >
        <CardHeader
          text={route.routeCode}
          contentLeft={
            <Icon icon={expanded ? stenaAngleDown : stenaAngleRight} />
          }
          contentRight={
            <Row alignItems={"center"}>
              <Txt>
                {pluralize(route.bookings.length, " booking", " bookings")}
              </Txt>
              <Space num={SpaceValues.SIXTEEN} />
              <Txt variant={"bold"}>
                {formatAmountWithCurrency(route.totalReserved, currencyCode)}
              </Txt>
            </Row>
          }
        />
      </ClickableCardHeader>
      <ExpandableContent expanded={expanded}>
        <div>
          {everExpanded && (
            <CardBody
              data-testid={`${testIdConstants.rebateBookingsRouteTable}-${route.routeCode}`}
            >
              {/* The expand animation clips its child, so the table needs its
                  own scroll container to stay reachable on narrow screens. */}
              <Box overflowX={"auto"}>
                <StandardTable
                  config={rebateBookingsTableConfig}
                  items={route.bookings}
                />
              </Box>
            </CardBody>
          )}
        </div>
      </ExpandableContent>
    </Card>
  );
};
